"use client";
// src/lib/sample-data/subscriptions.ts - Sample subscription data
import type { Subscription } from '@/lib/types';
import { format, subDays, subMonths } from 'date-fns';
// Note: User data now comes from Firebase Auth, using hardcoded user IDs for sample subscriptions
import { sampleClubsData } from './clubs';
import { SAMPLE_DATA_CONFIG, DEFAULT_CLUB } from './config';

const defaultOrganizationId = SAMPLE_DATA_CONFIG.defaultOrganizationId;
const defaultClubId = SAMPLE_DATA_CONFIG.defaultClubId;

export const sampleSubscriptionTemplatesData: Subscription[] = [
  // Club Membership Templates - All payments go to clubs through club billing accounts
  { 
    id: '2001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Topspin Club Basic Membership', 
    description: 'Basic membership for Topspin Tennis Club. Includes court booking rights and access to club events. User payments go directly to club billing account.', 
    type: 'Club Membership', 
    clubId: defaultClubId, 
    clubName: DEFAULT_CLUB.name, 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: 'Monthly', 
    status: 'Active', 
    autoRenew: true, 
    currencyCode: 'CHF', 
    membershipTiers: [ 
      { id: 'tier_club_001', name: 'Standard Adult', price: 60, description: 'Monthly access for one adult member.' }, 
      { id: 'tier_club_002', name: 'Junior (U18)', price: 35, description: 'Discounted monthly access for juniors under 18.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 2), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId, 
  },
  { 
    id: '3001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Topspin 10-Hour Booking Pass', 
    description: 'A flexible pass for 10 hours of court time at Topspin Tennis Club. Valid for 3 months from purchase. User payments go directly to club billing account.', 
    type: 'Booking Pass', 
    clubId: defaultClubId, 
    clubName: DEFAULT_CLUB.name, 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: '3-Months', 
    status: 'Active', 
    autoRenew: false, 
    currencyCode: 'CHF', 
    membershipTiers: [ 
      { id: 'tier_club_003', name: 'Standard Pass', price: 180, description: '10 hours of court time.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId, 
  },
  { 
    id: '5001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Alpine Sports Club Membership', 
    description: 'Access to Alpine Sports Center facilities including tennis and padel courts. Payments processed through club billing account.', 
    type: 'Club Membership', 
    clubId: '6473820195', 
    clubName: 'Alpine Sports Center', 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: 'Monthly', 
    status: 'Active', 
    autoRenew: true, 
    currencyCode: 'CHF', 
    membershipTiers: [ 
      { id: 'tier_club_004', name: 'Standard Adult', price: 75, description: 'Monthly access for one adult.' }, 
      { id: 'tier_club_005', name: 'Family (4p)', price: 180, description: 'Monthly access for up to 4 family members.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId, 
  },
  { 
    id: '6001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Lake Geneva Premium Membership', 
    description: 'Exclusive membership for Lake Geneva Tennis Club with lakeside privileges. Payments processed through club billing account.', 
    type: 'Club Membership', 
    clubId: '7584931206', 
    clubName: 'Lake Geneva Tennis Club', 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: 'Yearly', 
    status: 'Active', 
    autoRenew: false, 
    currencyCode: 'CHF', 
    membershipTiers: [ 
      { id: 'tier_club_006', name: 'Individual', price: 800, description: 'Annual individual access.' }, 
      { id: 'tier_club_007', name: 'Couple', price: 1400, description: 'Annual access for two adults.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId, 
  },
  { 
    id: '7001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Topspin Professional Training Pass', 
    description: 'Specialized pass for professional training sessions and coaching at Topspin Tennis Club. Payments processed through club billing account.', 
    type: 'Booking Pass', 
    clubId: defaultClubId, 
    clubName: DEFAULT_CLUB.name, 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: '6-Months', 
    status: 'Active', 
    autoRenew: false, 
    currencyCode: 'CHF', 
    membershipTiers: [ 
      { id: 'tier_club_008', name: 'Individual', price: 300, description: '6 months of training access.' }, 
      { id: 'tier_club_009', name: 'Group (5p)', price: 1200, description: '6 months of group training access.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId, 
  },
  // Global Padel Club subscriptions
  { 
    id: '8001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Global Padel Club Membership', 
    description: 'Premium padel club membership with access to all courts and facilities. Payments processed through club billing account.', 
    type: 'Club Membership', 
    clubId: '7384629150', 
    clubName: 'Global Padel Club', 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: 'Monthly', 
    status: 'Active', 
    autoRenew: true, 
    currencyCode: 'EUR', 
    membershipTiers: [ 
      { id: 'tier_club_010', name: 'Standard Adult', price: 85, description: 'Monthly access for one adult.' }, 
      { id: 'tier_club_011', name: 'Premium', price: 120, description: 'Premium access with additional benefits.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: '2951738460', 
    tenantId: '2951738460', 
  },
  // Elite Tennis Academy subscriptions
  { 
    id: '9001234567890', 
    userUid: undefined, 
    userName: '', 
    userEmail: undefined, 
    title: 'Elite Tennis Academy Membership', 
    description: 'Professional tennis training and court access at Elite Tennis Academy. Payments processed through club billing account.', 
    type: 'Club Membership', 
    clubId: '4629385170', 
    clubName: 'Elite Tennis Academy', 
    startDate: format(new Date(), 'yyyy-MM-dd'), 
    duration: 'Monthly', 
    status: 'Active', 
    autoRenew: true, 
    currencyCode: 'USD', 
    membershipTiers: [ 
      { id: 'tier_club_012', name: 'Standard', price: 150, description: 'Monthly access and basic training.' }, 
      { id: 'tier_club_013', name: 'Professional', price: 300, description: 'Professional training and unlimited court access.' }, 
    ], 
    dateCreated: format(subMonths(new Date(), 1), 'yyyy-MM-dd'), 
    dateUpdated: format(new Date(), 'yyyy-MM-dd'), 
    organizationId: '6174829350', 
    tenantId: '6174829350', 
  },
];

export const sampleSubscriptionsData: Subscription[] = [
  ...sampleSubscriptionTemplatesData,
  // Active user subscriptions - All linked to clubs for Version 2 billing architecture
  { 
    ...sampleSubscriptionTemplatesData[0], // Topspin Club Basic Membership
    id: '1101234567890', // Unique ID for Roger Federer
    userUid: '2847391650', // Roger Federer 
    userName: 'Roger Federer', 
    userEmail: 'roger.federer@example.com', 
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'), 
    status: 'Active', 
    autoRenew: true, 
    organizationId: defaultOrganizationId 
  },
  { 
    ...sampleSubscriptionTemplatesData[0], // Topspin Club Basic Membership
    id: '1201234567890', // Unique ID for Andy Murray
    userUid: '9617384052', // Andy Murray 
    userName: 'Andy Murray', 
    userEmail: 'andy.murray@example.com', 
    startDate: format(subDays(new Date(), 45), 'yyyy-MM-dd'), 
    status: 'Active', 
    autoRenew: true, 
    organizationId: defaultOrganizationId 
  },
  { 
    ...sampleSubscriptionTemplatesData[1], // Topspin 10-Hour Booking Pass
    id: '1301234567890', // Unique ID for Jannik Sinner
    userUid: '1728495063', // Jannik Sinner 
    userName: 'Jannik Sinner', 
    userEmail: 'jannik.sinner@example.com', 
    startDate: format(subDays(new Date(), 10), 'yyyy-MM-dd'), 
    status: 'Active', 
    autoRenew: false, 
    organizationId: defaultOrganizationId 
  },
  { 
    ...sampleSubscriptionTemplatesData[0], // Topspin Club Basic Membership
    id: '1401234567890', // Unique ID for Coco Gauff
    userUid: '2839506174', // Coco Gauff 
    userName: 'Coco Gauff', 
    userEmail: 'coco.gauff@example.com', 
    clubId: defaultClubId, 
    clubName: sampleClubsData.find(c => c.id === defaultClubId)?.name, 
    startDate: format(subDays(new Date(), 5), 'yyyy-MM-dd'), 
    status: 'Active', 
    autoRenew: true, 
    organizationId: defaultOrganizationId 
  },
  { 
    ...sampleSubscriptionTemplatesData[2], // Alpine Sports Club Membership
    id: '1501234567890', // Unique ID for Iga Świątek
    userUid: '3940617285', // Iga Świątek 
    userName: 'Iga Świątek', 
    userEmail: 'iga.swiatek@example.com', 
    startDate: format(subDays(new Date(), 90), 'yyyy-MM-dd'), 
    status: 'Active', 
    autoRenew: false, 
    organizationId: defaultOrganizationId 
  },
].map(s => ({ ...s, organizationId: defaultOrganizationId } as Subscription));

export const sampleSubscriptions: Subscription[] = [
  {
    id: '1601234567890', // Unique ID for sample user
    userUid: '2847391650', // Roger Federer's fixed UID
    userName: 'Sample User',
    userEmail: 'sample.user@example.com',
    title: 'Annual Standard Pass',
    description: 'Access to all standard facilities and values.',
    type: 'Club Membership',
    clubId: defaultClubId,
    clubName: sampleClubsData.find(club => club.id === defaultClubId)?.name,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    duration: 'Monthly',
    status: 'Active',
    autoRenew: true,
    currencyCode: 'CHF',
    organizationId: defaultOrganizationId,
    tenantId: defaultOrganizationId,
    membershipTiers: [
      {
        id: 'tier_010',
        name: 'Standard',
        price: 100,
        description: 'Basic membership tier'
      }
    ]
  },
  {
    id: '1701234567890', // Unique ID for junior user
    userUid: '6182947350', // Serena Williams' fixed UID
    userName: 'Junior User',
    userEmail: 'junior.user@example.com',
    title: 'Junior Tennis Program',
    description: 'Special membership for young tennis enthusiasts.',
    type: 'Club Membership',
    clubId: defaultClubId,
    clubName: sampleClubsData.find(c => c.id === defaultClubId)?.name,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    duration: 'Monthly',
    status: 'Active',
    autoRenew: true,
    currencyCode: 'CHF',
    organizationId: defaultOrganizationId,
    tenantId: defaultOrganizationId,
    membershipTiers: [
      {
        id: 'tier_011',
        name: 'Junior',
        price: 50,
        description: 'Junior membership tier'
      }
    ]
  },
  // Add more subscriptions as needed
];
